import {resolveBackground, setAlpha} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {
    generateSportFilter,
    generateSportFilterCircleRow,
} from '#core/application/modules/tiles/sport-filter-circle-button/index.js';
import {generateEventsCoupon} from '#core/application/modules/tiles/events-coupon/index.js';

function generateProSectionContainerStyle(SECTION_CONTAINER_BG, SECTION_CONTAINER_BORDER_COLOR, ACCENT_COLOR) {
    return {
        borderRadius: 22,
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: SECTION_CONTAINER_BORDER_COLOR,
        ...resolveBackground(SECTION_CONTAINER_BG),
        overflow: 'hidden',
        boxSizing: 'border-box',
        width: '100%',
        padding: '0 0 8px',
        backgroundImage: `linear-gradient(90deg, transparent 0%, ${ACCENT_COLOR} 25%, ${ACCENT_COLOR} 75%, transparent 100%)`,
        backgroundSize: '100% 3px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top',
    };
}

function generateSportsFilterContainerWithSection(PALETTE, ACCENT_COLOR) {
    return {
        minSportsCount: 2,
        container: {
            itemSpacing: 8,
            style: generateProSectionContainerStyle(
                PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_CONTAINER_BG,
                PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_CONTAINER_PANEL_BORDER_COLOR,
                ACCENT_COLOR
            ),
        },
        filterRow: {
            style: {
                padding: '0 8px',
            },
        },
        sportsFilter: generateSportFilter(PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_SPORT_FILTER_CIRCLE_BUTTON),
        sectionIconImage: {
            style: {
                padding: '6px',
                borderRadius: '50%',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: setAlpha(ACCENT_COLOR, 0.3),
                backgroundColor: setAlpha(ACCENT_COLOR, 0.16),
                stroke: ACCENT_COLOR,
                fill: ACCENT_COLOR,
            },
        },
        sectionTitle: {
            style: {
                ...PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_CONTAINER_TITLE_TEXT,
            },
        },
        contentContainer: {
            style: {
                padding: '0 8px',
            },
        },
    };
}

export function generateProTopSportsViewConfig(PALETTE) {
    const SECTION_CONTAINER_STYLE = generateProSectionContainerStyle(
        PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_CONTAINER_BG,
        PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_CONTAINER_PANEL_BORDER_COLOR
    );

    return {
        showMenuSportIcon: true,
        showCouponSportIcon: true,
        eventCounter: {
            headerPerSport: false,
        },
        mainContainer: {
            style: {
                flexDirection: 'column',
                ...SECTION_CONTAINER_STYLE,
                _mobile_padding: SECTION_CONTAINER_STYLE.padding,
                backgroundImage: 'none',
            },
        },
        style: {
            navigationTitle: {
                ...PALETTE.HOME_PAGE_AVANTGARDE_CONTENT_SECTION_CONTAINER_TITLE_TEXT,
            },
        },
        couponContainer: {
            style: {
                flexDirection: 'column',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                borderRadius: '0',
            },
        },
        navigationMenuContainer: {
            style: {
                padding: 8,
                boxSizing: 'border-box',
                maxWidth: '100%',
            },
        },
        navigationMenu: generateSportFilterCircleRow(PALETTE.HOME_PAGE_AVANTGARDE_SPORT_FILTER_CIRCLE_BUTTON),
        navigationMenuItemWrapper: {
            style: {
                minWidth: '36px',
                alignItems: 'center',
                justifyContent: 'center',
            },
        },
        navigationMenuSkeleton: {
            linkPlaceholder: {
                style: {
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                },
            },
        },
        eventsCoupon: generateEventsCoupon(PALETTE.HOME_PAGE_AVANTGARDE_TOP_SPORTS_COUPON),
    };
}

export function homePageAvantgardeFactory(PALETTE) {
    const PRO_CARDS_CAROUSEL_STYLE = {
        headerContainer: {
            style: {
                _mobile_padding: '16px 0 8px',
            },
        },
        swiper: {
            style: {
                _mobile_padding: '5px 0 10px',
            },
        },
    };

    return {
        HomePageAvantgarde: {
            feedView: {
                cardList: {
                    style: {
                        padding: 0,
                        _mobile_padding: '0 8px',
                    },
                },
            },
            proView: {
                layoutParts: {
                    EventCardsCarousel: {
                        awaitPromotions: false,
                        postponed: false,
                    },
                },
                container: {
                    itemSpacing: 8,
                    style: {
                        padding: 0,
                        _mobile_padding: '0 8px',
                    },
                },
                eventCardsCarousel: PRO_CARDS_CAROUSEL_STYLE,
                propCardsCarousel: PRO_CARDS_CAROUSEL_STYLE,
                propCardsCarouselSportsFilter: generateSportsFilterContainerWithSection(
                    PALETTE,
                    PALETTE.HOME_PAGE_AVANTGARDE_PROP_CARDS_SPORT_FILTER_SECTION_ACCENT_COLOR
                ),
                prebuiltBetBuilderPromotionCardsSportsFilter: generateSportsFilterContainerWithSection(
                    PALETTE,
                    PALETTE.HOME_PAGE_AVANTGARDE_PREBUILT_SPORT_FILTER_SECTION_ACCENT_COLOR
                ),
                prebuiltAccaPromotionCardsSportsFilter: generateSportsFilterContainerWithSection(
                    PALETTE,
                    PALETTE.HOME_PAGE_AVANTGARDE_PREBUILT_SPORT_FILTER_SECTION_ACCENT_COLOR
                ),
                topSportCoupons: {
                    primary: {
                        coupon: {
                            view: merge(generateProTopSportsViewConfig(PALETTE), {
                                eventCounter: {
                                    headerPerSport: false,
                                    _mobile_headerTotal: true,
                                    footerSelectedSport: true,
                                },
                            }),
                        },
                    },
                    secondary: {
                        coupon: {
                            view: generateProTopSportsViewConfig(PALETTE),
                        },
                    },
                },
            },
            sportsFilter: {
                container: {
                    style: {
                        padding: 0,
                    },
                },
                filterRow: {
                    style: {
                        padding: 0,
                        _mobile_padding: '0 8px',
                    },
                },
                sportsFilter: merge(generateSportFilter(PALETTE.HOME_PAGE_AVANTGARDE_SPORT_FILTER_CIRCLE_BUTTON), {
                    sportsMenuRow: {
                        item: {
                            selected: {
                                style: {
                                    base: {
                                        width: 'initial',
                                        minWidth: 'initial',
                                        padding: '0 8px',
                                    },
                                },
                            },
                        },
                        itemInnerContainer: {
                            itemSpacing: 8,
                        },
                    },
                }),
            },
        },
        SportsFilterContainer: {
            minSportsCount: 1,
        },
    };
}
